import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';

import Loading from '@/components/common/Loading';
import {
  type CombinedTaskItem,
  DEFAULT_PAGE_SIZE,
  type MediaItem,
  useTaskList,
} from '@/components/DrawerTaskPanel/useTaskList';
import { useTranslate } from '@/hooks/useTranslate';
import { VIRAL_TEMPLATE_USE_MOCK } from '@/services/viralTemplate';
import { getFeatureTypes, viralTemplateURL } from '@/utils/router';

import ViralMyCreationTile from './ViralMyCreationTile';

interface MyCreationsProps {
  isLoggedIn?: boolean;
  active?: boolean;
}

interface GridCell {
  batch: CombinedTaskItem;
  media: MediaItem;
}

function dayBucketKey(createTime: number) {
  const d = new Date(createTime);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

const MyCreations = memo(({ isLoggedIn, active = false }: MyCreationsProps) => {
  const { t } = useTranslate();
  const intl = useIntl();
  const scrollRef = useRef<HTMLDivElement>(null);
  const previousVisiblePagesRef = useRef<Set<number>>(new Set());

  const queryTypes = useMemo(() => getFeatureTypes(viralTemplateURL), []);
  const {
    taskList,
    loading,
    hasMore,
    loadMore,
    refresh,
    updateMediaItemTitle,
    deleteTask,
    deleteMediaItem,
    setVisiblePages,
  } = useTaskList({
    queryTypes,
    pathname: viralTemplateURL,
    active,
  });

  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(() => {
    if (!active || !isLoggedIn) return;
    refreshRef.current();
  }, [active, isLoggedIn]);

  // 触底加载
  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || loading || !hasMore) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 100) {
      void loadMore();
    }
  }, [loading, hasMore, loadMore]);

  // 展平 batch → gridCells
  const gridCells = useMemo(() => {
    const cells: GridCell[] = [];
    for (const batch of taskList) {
      for (const media of batch.mediaItems) {
        cells.push({ batch, media });
      }
    }
    return cells;
  }, [taskList]);

  // 每个 batch 对应的虚拟页码（用于 IntersectionObserver 驱动轮询）
  const batchPageNum = useMemo(() => {
    const order: number[] = [];
    const seen = new Set<number>();
    for (const { batch } of gridCells) {
      if (!seen.has(batch.id)) {
        seen.add(batch.id);
        order.push(batch.id);
      }
    }
    const map = new Map<number, number>();
    order.forEach((id, i) => {
      map.set(id, Math.floor(i / DEFAULT_PAGE_SIZE) + 1);
    });
    return map;
  }, [gridCells]);

  // 按天分组
  const daySections = useMemo(() => {
    const buckets = new Map<string, GridCell[]>();
    for (const cell of gridCells) {
      const k = dayBucketKey(cell.batch.createTime);
      if (!buckets.has(k)) buckets.set(k, []);
      buckets.get(k)!.push(cell);
    }
    const keys = [...buckets.keys()].sort((a, b) => (a < b ? 1 : a > b ? -1 : 0));
    return keys.map((dayKey) => {
      const cells = buckets.get(dayKey)!;
      const label = intl.formatDate(new Date(cells[0].batch.createTime), {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      return { dayKey, label, cells };
    });
  }, [gridCells, intl]);

  // IntersectionObserver 追踪可视页码
  useEffect(() => {
    const container = scrollRef.current;
    if (!container || gridCells.length === 0) return;

    const visiblePagesSet = new Set<number>();

    const isSetsEqual = (a: Set<number>, b: Set<number>) => {
      if (a.size !== b.size) return false;
      for (const v of a) if (!b.has(v)) return false;
      return true;
    };

    const syncVisiblePages = () => {
      if (visiblePagesSet.size > 0) {
        const next = new Set(visiblePagesSet);
        if (!isSetsEqual(next, previousVisiblePagesRef.current)) {
          previousVisiblePagesRef.current = next;
          setVisiblePages(next);
        }
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        let changed = false;
        for (const entry of entries) {
          const page = Number(entry.target.getAttribute('data-page'));
          if (isNaN(page)) continue;
          if (entry.isIntersecting) {
            if (!visiblePagesSet.has(page)) {
              visiblePagesSet.add(page);
              changed = true;
            }
          } else {
            // 同一 page 可能有多个 tile，只在全部离开时移除
            const siblings = container.querySelectorAll(`[data-page="${page}"]`);
            const containerRect = container.getBoundingClientRect();
            let anyVisible = false;
            siblings.forEach((el) => {
              if (el === entry.target) return;
              const r = el.getBoundingClientRect();
              if (r.top < containerRect.bottom && r.bottom > containerRect.top)
                anyVisible = true;
            });
            if (!anyVisible && visiblePagesSet.has(page)) {
              visiblePagesSet.delete(page);
              changed = true;
            }
          }
        }
        if (changed) syncVisiblePages();
      },
      { root: container, rootMargin: '0px', threshold: 0 },
    );

    const tiles = container.querySelectorAll('[data-viral-creation-tile][data-page]');
    tiles.forEach((el) => observer.observe(el));

    // 初始可见检测
    const containerRect = container.getBoundingClientRect();
    tiles.forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.top < containerRect.bottom && r.bottom > containerRect.top) {
        const page = Number(el.getAttribute('data-page'));
        if (!isNaN(page)) visiblePagesSet.add(page);
      }
    });
    syncVisiblePages();

    return () => observer.disconnect();
  }, [gridCells.length, setVisiblePages]);

  // --- 渲染 ---

  if (!isLoggedIn && !VIRAL_TEMPLATE_USE_MOCK) {
    return (
      <div className="flex h-full w-full items-center justify-center text-[#6b6b6b]">
        {t('18432c5cd309e3b1e5dda1b438381f5d')}
      </div>
    );
  }

  if (loading && gridCells.length === 0) {
    return (
      <div className="flex min-h-[280px] w-full flex-1 items-center justify-center">
        <Loading size={50} />
      </div>
    );
  }

  if (!loading && gridCells.length === 0) {
    return (
      <div className="flex min-h-[280px] w-full flex-1 items-center justify-center text-[14px] text-[#11111199]">
        {t('18432c5cd309e3b1e5dda1b438381f5d')}
      </div>
    );
  }

  return (
    <div
      ref={scrollRef}
      className="scroll-overlay flex h-full min-h-0 flex-col overflow-y-auto no-scrollbar"
      onScroll={onScroll}
    >
      <div className="flex flex-col gap-8">
        {daySections.map((section) => (
          <section key={section.dayKey}>
            <h2 className="sticky top-0 z-[1] mb-3 bg-white/95 py-1 text-left text-[15px] font-semibold leading-6 text-[#111] backdrop-blur-sm">
              {section.label}
            </h2>
            <div
              className="grid w-full gap-[16px]"
              style={{ gridTemplateColumns: 'repeat(auto-fill, 180px)' }}
            >
              {section.cells.map(({ batch, media }) => (
                <div
                  key={`${batch.id}-${media.id}`}
                  data-viral-creation-tile
                  data-page={batchPageNum.get(batch.id) ?? 1}
                >
                  <ViralMyCreationTile
                    batch={batch}
                    media={media}
                    onRename={(mediaItemId, newTitle) =>
                      updateMediaItemTitle(batch.id, mediaItemId, newTitle)
                    }
                    onDeleteMedia={(mediaItemId) =>
                      deleteMediaItem(batch.id, mediaItemId)
                    }
                    onDeleteBatch={() => deleteTask(batch.id)}
                  />
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>

      {loading && taskList.length > 0 ? (
        <div className="flex justify-center py-[16px]">
          <Loading size={40} />
        </div>
      ) : null}
    </div>
  );
});

MyCreations.displayName = 'ViralTemplateMyCreations';

export default MyCreations;
